import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Card, Typography, Box, useTheme, CircularProgress } from "@mui/material";

const VideoPlayerCard = forwardRef(({
  interview,
  videoBlobUrl,
  questionText,
  transcriptText,
  autoPlayKey,
  onVideoPlay,
  loading = false,
  showNoVideo = false,
}, ref) => {
  const theme = useTheme();
  const videoRef = useRef(null);

  // Expose a minimal control surface to the parent (pause only for now)
  useImperativeHandle(ref, () => ({
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    getVideoElement: () => videoRef.current,
  }));

  const hasVideo =
    videoBlobUrl &&
    typeof videoBlobUrl === "string" &&
    videoBlobUrl.trim() !== "";

  const hasQuestion =
    questionText &&
    typeof questionText === "string" &&
    questionText.trim() !== "";

  const hasTranscript =
    transcriptText &&
    typeof transcriptText === "string" &&
    transcriptText.trim() !== "";
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !videoBlobUrl) return;
    
      video.load();   // forces browser to start downloading
    }, [videoBlobUrl]);
    
  // When question changes, pause video so it doesn’t play over the new question’s TTS
  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl) {
      videoEl.pause();
    }
  }, [videoBlobUrl]);

  // Only start video when TTS has finished (autoPlayKey increments after onTtsEnd)
  // Do not depend on videoBlobUrl here, so switching questions doesn’t start video until TTS ends
  useEffect(() => {
    if (!hasVideo || !autoPlayKey) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch((err) => {
        // eslint-disable-next-line no-console
        console.debug("Video autoplay blocked or failed:", err);
      });
    }
  }, [autoPlayKey, hasVideo]);

  return (
    <Card
      elevation={0}
      sx={{
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      {loading ? (
        <Box
          sx={{
            aspectRatio: "16/9",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(15,23,42,0.9)"
                : "#f3f4f6",
          }}
        >
          <CircularProgress size={32} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Loading recording...
          </Typography>
        </Box>
      ) : hasVideo ? (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%",
            backgroundColor: "#000",
          }}
        >
          <video
            ref={videoRef}
            key={videoBlobUrl}
            src={videoBlobUrl}
            controls
            playsInline
            //crossOrigin="anonymous"
            preload="auto"   // ⭐ IMPORTANT
  onLoadedMetadata={() => {
    videoRef.current.currentTime = 0;
  }}
  onPlay={() => onVideoPlay?.()}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
            
          />
        </Box>
      ) : showNoVideo ? (
        <Box
          sx={{
            aspectRatio: "16/9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "#f3f4f6",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No recording available.
          </Typography>
        </Box>
      ) : null}

      {/* {hasQuestion && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.2)"
                : "rgba(0,0,0,0.02)",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ display: "block", mb: 0.5 }}
          >
            Question
          </Typography>
          <Typography
            variant="caption"
            sx={{
              
              color: theme.palette.text.primary,
              minHeight: "2em",
              overflowWrap: "anywhere",
            }}
          >
            {questionText}
          </Typography>
        </Box>
      )} */}

      {hasTranscript && (
        <Box
          sx={{
            p: 2,
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.3)"
                : "rgba(0,0,0,0.01)",
          }}
        >
          <Typography
            variant="body2"
            
            fontWeight={'bold'}
            sx={{ display: "block", mb: 0.5 }}
          >
            Transcript
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              maxHeight: 250,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {transcriptText}
          </Typography>
        </Box>
      )}
    </Card>
  );
});

VideoPlayerCard.displayName = "VideoPlayerCard";

export default VideoPlayerCard;
